/**
 * @file footer-columns.ts
 * @description Колонки подвала «Колонками» в кабинете (phase19.16, приёмка
 * Р2а; SB113 — колонки задаёт меню, лимит 3, пустая колонка в лимите;
 * SB114 — колонка «О магазине»; SB115 — дубли ссылок платформы;
 * uiux-footer-columns §4.4–§4.7, §5).
 *
 * Обёртка над общим `footerLayout` из `@chaiberi/shared` (один подсчёт для
 * кабинета, сервера и витрины): что видно покупателю, кабинет знает по
 * `target.status` пункта; цели ссылок платформы — по выбранной странице
 * «Условия доставки и оплаты» и документу «Условия возврата». Здесь же —
 * подписи схемы («авто, 2 кат.», «4 пункта») и тексты ошибок меню подвала.
 */

import {
  footerLayout,
  type FooterAboutInput,
  type FooterAboutPart,
  type FooterLayout,
  type FooterLayoutColumn,
  type FooterPlatformTargets,
  type SiteTheme,
} from "@chaiberi/shared";

import { ApiError } from "../api-client";
import type { SiteBrandDraft, SiteMenuItem } from "./types";

/** Заголовок колонки в редакторе: счётчик «N/40» (на сайте длинный переносится, SB113). */
export const FOOTER_COLUMN_TITLE_MAX = 40;
/** Длиннее — подсказка «перенесётся на 2 строки» (§5). */
export const FOOTER_COLUMN_TITLE_SOFT = 24;
/** Автосписок длиннее — мягкая подсказка «лучше до 8 категорий» (§5, вопрос 11). */
export const FOOTER_AUTO_SOFT_MAX = 8;

/** Пометка у пункта-дубля ссылки платформы (SB115: запрета нет). */
export const FOOTER_DUPLICATE_NOTE =
  "Эта ссылка уже есть внизу подвала — её ставит платформа; на сайте там она скроется, пока пункт в меню.";

/** Что известно кабинету для подсчёта колонок. */
export interface FooterLayoutContext {
  footer: SiteTheme["footer"];
  brand: SiteBrandDraft;
  /** id документа «Условия возврата» (`returns-notice`); null — нет или не загружен. */
  returnsDocId: string | null;
  /** Сколько категорий соберёт автосписок; не задано — неизвестно. */
  catalogCategoriesCount?: number;
}

/** Контактов, которые попадут в подвал: «показывать в подвале» и есть значение (email — из магазина). */
export function footerContactsCount(brand: SiteBrandDraft): number {
  return brand.contacts.filter(
    (c) => c.showInFooter && ((c.value ?? "").trim() !== "" || (c.kind === "email" && !!brand.shopContactEmail)),
  ).length;
}

/** Части «О магазине» по флажкам `footer.show` и данным бренда. */
export function footerAboutInput(brand: SiteBrandDraft, footer: SiteTheme["footer"]): FooterAboutInput {
  const footerContacts = footerContactsCount(brand);
  return {
    show: { brand: footer.show.brand, tagline: footer.show.tagline, contacts: footer.show.contacts, signature: footer.show.signature },
    hasBrand: brand.siteName.trim() !== "" || brand.logoLight !== null || brand.logoDark !== null,
    tagline: brand.tagline,
    footerContacts,
    workingHours: brand.workingHours,
    signature: brand.footerSignature,
  };
}

/** Цели ссылок платформы для сравнения «по цели» (SB115). «Личный кабинет» shared подставляет сам. */
export function footerPlatformTargets(brand: SiteBrandDraft, returnsDocId: string | null): FooterPlatformTargets {
  const page = brand.deliveryTermsPage;
  return {
    delivery: page?.isPublished ? { sitePageId: page.id, hrefs: [`/${page.slug}`] } : null,
    returns: returnsDocId ? { legalDocumentId: returnsDocId, hrefs: ["/legal/returns-notice"] } : null,
  };
}

/** Раскладка подвала по меню кабинета. */
export function cabinetFooterLayout(items: readonly SiteMenuItem[], ctx: FooterLayoutContext): FooterLayout<SiteMenuItem> {
  return footerLayout(items, {
    isVisible: (n) => !n.isHidden && n.target.status !== "deleted" && n.target.status !== "unpublished",
    hrefOf: (n) => n.target.href,
    catalogCategoriesCount: ctx.catalogCategoriesCount,
    platformLinks: footerPlatformTargets(ctx.brand, ctx.returnsDocId),
    menuShown: ctx.footer.show.menu,
    about: footerAboutInput(ctx.brand, ctx.footer),
  });
}

/** Подписи частей «О магазине» для схемы и карточки. */
export const FOOTER_ABOUT_PART_LABELS: Readonly<Record<FooterAboutPart, string>> = {
  brand: "лого",
  tagline: "слоган",
  contacts: "контакты",
  hours: "часы",
  signature: "подпись",
};

/** «лого, слоган, подпись»; пусто — null. */
export function aboutSummary(parts: readonly FooterAboutPart[]): string | null {
  return parts.length > 0 ? parts.map((p) => FOOTER_ABOUT_PART_LABELS[p]).join(", ") : null;
}

/** Склонение: 1 пункт, 2 пункта, 5 пунктов. */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/** Заголовок колонки для схемы и карточки. */
export function columnTitle(column: FooterLayoutColumn<SiteMenuItem>): string {
  return column.title ?? "без заголовка";
}

/** Что в колонке: «авто, 2 кат.», «4 пункта (1 скрыт)», «пустая». */
export function columnSummary(column: FooterLayoutColumn<SiteMenuItem>, categoriesKnown: boolean): string {
  if (column.kind === "auto-categories") return categoriesKnown ? `авто, ${column.visibleCount} кат.` : "авто";
  if (column.items.length === 0) return "пустая";
  const n = column.items.length;
  const hidden = n - column.visibleCount;
  return `${n} ${plural(n, "пункт", "пункта", "пунктов")}${hidden > 0 ? ` (${hidden} скрыт)` : ""}`;
}

/** Ошибки меню подвала (400 `site_menu_invalid`, `details.issues[].code`) — что делать дальше. */
const FOOTER_ISSUE_TEXT: Readonly<Record<string, string>> = {
  site_menu_footer_loose_item: "В подвале пункт должен стоять в колонке. Добавьте его через «+ Пункт в колонку».",
  site_menu_footer_columns:
    "Колонок меню — не больше 3: в четыре колонки подвал на ПК ещё читается, в пять — уже нет. Объедините колонки или удалите лишнюю.",
};

/** Свой текст для ошибки меню подвала или null (тогда — общий текст сервера). */
export function footerMenuIssueText(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.code !== "site_menu_invalid") return null;
  const details = error.details;
  const issues = typeof details === "object" && details !== null ? (details as { issues?: unknown }).issues : null;
  if (!Array.isArray(issues)) return null;
  for (const issue of issues) {
    const code = typeof issue === "object" && issue !== null ? (issue as { code?: unknown }).code : null;
    if (typeof code === "string" && FOOTER_ISSUE_TEXT[code]) return FOOTER_ISSUE_TEXT[code];
  }
  return null;
}
